import useSwr from 'swr'
import { API_URL } from '@/utils/constants'

/// Ranges the API understands, cut at New York midnight. Bucket size is
/// chosen server-side: today/yesterday → hours, 7d/30d/90d → days,
/// ytd/1y → weeks, all → months.
export const ADMIN_RANGES = ['today', 'yesterday', '7d', '30d', '90d', 'ytd', '1y', 'all'] as const
export type AdminRange = (typeof ADMIN_RANGES)[number]

export interface AdminTotals {
	sales: number
	volume_sats: number
	revenue_sats: number
	sniped_sales: number
	sniped_volume_sats: number
	missed_revenue_sats: number
	unaudited_sales: number
	buyers: number
	sellers: number
	unique_users: number
	new_wallets: number
	new_buyers: number
	listings: number
	listers: number
	/** Paid inscribe orders (one order can hold many inscriptions). */
	inscriptions: number
	/** Inscriptions across those orders. Absent on older API builds. */
	inscribe_items?: number
	inscribe_revenue_sats: number
	/** MoonPay affiliate payouts booked in the window (USDC/USDT ≈ USD). */
	moonpay_usd: number
	moonpay_payouts: number
	/** Flat service fees paid on-chain to the tool-fee address. */
	tool_revenue_sats?: number
	tool_fee_txs?: number
	/** DIESEL minted into the OW vault by those transactions (8-decimal base units) and its USD value. */
	diesel_minted_units?: number
	diesel_usd?: number
	/** Audit-measured marketplace fee actually lost to snipes (spender-group deduped). */
	audit_shortfall_sats?: number
	audit_sniped_groups?: number
	audited_sales?: number
}

export interface AdminSeriesPoint {
	t: string
	sales: number
	volume_sats: number
	revenue_sats: number
	sniped_sales: number
	sniped_volume_sats: number
	missed_revenue_sats: number
	unaudited_sales: number
	buyers: number
	sellers: number
	unique_users: number
	new_wallets: number
	new_buyers: number
	listings: number
	inscriptions: number
	inscribe_revenue_sats: number
	moonpay_usd: number
	moonpay_payouts: number
	/** Flat service fees paid on-chain to the tool-fee address. */
	tool_revenue_sats?: number
	tool_fee_txs?: number
	/** DIESEL minted into the OW vault by those transactions (8-decimal base units) and its USD value. */
	diesel_minted_units?: number
	diesel_usd?: number
	audit_shortfall_sats?: number
	audit_sniped_groups?: number
	audited_sales?: number
}

export interface AdminAuditV2 {
	audited_sales: number
	sniped_sales: number
	backlog: number
	oldest_audited_at: string | null
	last_audit_run_at: string | null
	shortfall_all_time_sats: number
	expected_fee_all_time_sats: number
}

export interface AdminFeeWallet {
	key: 'marketplace' | 'inscribe' | 'tools' | string
	label: string
	address: string
	available: boolean
	confirmed_sats?: number
	mempool_delta_sats?: number
	received_all_time_sats?: number
	tx_count?: number
}

export interface AdminMoonpay {
	available: boolean
	all_time_usd?: number
	payout_count?: number
	last_payout_at?: string | null
	last_payout_usd?: number | null
	last_payout_remark?: string | null
	source?: string
	/** Payouts landing in the selected window, newest first. */
	payouts?: AdminMoonpayPayout[]
}

export interface AdminMoonpayPayout {
	posted_at: string
	usd: number
	remark: string
}

/** One completed sale and the marketplace fee it paid. */
export interface AdminFeeItem {
	inscription_id: string | null
	price_sats: number
	fee_sats: number
	fee_rate: number | null
	bought_at: string | null
	status: string | null
	buyer_address: string | null
	seller_address: string | null
	purchase_txid: string | null
	purchase_source: string | null
	collection_slug: string | null
	collection_name: string | null
	collection_icon: string | null
}

/** What an inscribe order was for, read from its first inscription. */
export interface AdminWalletFlow {
	attempts: number
	/** Wallet-attributable failures (the fail rate's numerator). */
	failed: number
	cancelled: number
	/** Our side unavailable: deploy blip, index lag, network. */
	platform?: number
	/** Seller-facing rule with a fallback or a clear next step. */
	guard?: number
	failure_rate: number | null
	previous_failure_rate: number | null
	previous_attempts?: number | null
}

export interface AdminWalletCode {
	code: string
	phase: string
	flow: 'listing' | 'purchase'
	failures: number
	bucket?: 'wallet' | 'platform' | 'guard' | 'cancelled'
}

export interface AdminWalletProvider {
	provider: string
	attempts: number
	failed: number
	cancelled: number
	platform?: number
	guard?: number
	failure_rate: number | null
	flows: Partial<Record<'listing' | 'purchase', AdminWalletFlow>>
	top_codes: AdminWalletCode[]
}

export interface AdminWalletHealth {
	available: boolean
	providers: AdminWalletProvider[]
	top_codes: Array<{
		code: string
		failures: number
		bucket?: 'wallet' | 'platform' | 'guard' | 'cancelled'
		providers: Array<{ provider: string; failures: number }>
	}>
	note?: string
}

export interface AdminToolFeePurpose {
	purpose: string
	label: string
	txs: number
	sats: number
}

export interface AdminToolFeeEvent {
	txid: string
	purpose: string
	label: string
	payer: string | null
	sats: number
	at: string | null
	confirmed: boolean
	block_height: number | null
}

export type AdminInscribeKind = 'token' | 'child' | 'delegate' | 'file'

export interface AdminInscribeOrder {
	paid_at: string | null
	payment_txid: string | null
	items: number
	fee_sats: number
	fee_rate: number | null
	bytes: number
	confirmed: boolean | null
	content_type: string | null
	parent_inscription_id: string | null
	first_inscription_id: string | null
	kind: AdminInscribeKind
	label: string
	protocol: string | null
	op: string | null
	tick: string | null
	amt: string | null
}

export interface AdminInscribeSource {
	key: string
	kind: AdminInscribeKind
	label: string
	protocol: string | null
	op: string | null
	tick: string | null
	orders: number
	items: number
	fee_sats: number
}

export interface AdminInscribe {
	orders: AdminInscribeOrder[]
	sources: AdminInscribeSource[]
	/** Orders the attribution covers (the window's most recent ones). */
	classified_orders: number
	/** True when the window has more orders than were attributed. */
	capped: boolean
}

export interface AdminSource {
	source: string
	sales: number
	volume_sats: number
	revenue_sats: number
	sniped_sales: number
}

export interface AdminTopCollection {
	slug: string | null
	name: string | null
	icon: string | null
	sales: number
	volume_sats: number
	revenue_sats: number
	missed_revenue_sats: number
}

export interface AdminSnipe {
	inscription_id: string | null
	price_sats: number
	missed_revenue_sats: number
	bought_at: string | null
	buyer_address: string | null
	seller_address: string | null
	purchase_txid: string | null
	purchase_source: string | null
	collection_slug: string | null
	collection_name: string | null
}

export interface AdminSnipeCollection {
	slug: string | null
	name: string | null
	icon: string | null
	sniped_sales: number
	sniped_volume_sats: number
	missed_revenue_sats: number
}

export interface AdminListingTotals {
	listings: number
	listers: number
	/** Distinct inscriptions behind those listing events (relists count once). */
	items: number
	/** Summed ask, excluding asks above `moon_threshold_sats`. */
	value_sats: number
	median_sats: number | null
	moon_listings: number
	sold: number
	sold_value_sats: number
	active: number
	active_value_sats: number
	cancelled: number
}

export interface AdminListingPoint {
	t: string
	listings: number
	value_sats: number
	moon_listings: number
	sold: number
	listers: number
}

export interface AdminListingCollection {
	slug: string | null
	name: string | null
	icon: string | null
	kind: 'collection' | 'runes' | 'uncollected'
	listings: number
	listers: number
	items: number
	value_sats: number
	median_sats: number | null
	floor_sats: number | null
	moon_listings: number
	sold: number
	sold_value_sats: number
	active: number
}

export interface AdminTopListing {
	inscription_id: string | null
	price_sats: number
	created: string
	seller_address: string | null
	collection_slug: string | null
	collection_name: string | null
}

export interface AdminListings {
	available: boolean
	moon_threshold_sats?: number
	summary?: AdminListingTotals
	previous?: Pick<
		AdminListingTotals,
		'listings' | 'listers' | 'value_sats' | 'moon_listings'
	> | null
	series?: AdminListingPoint[]
	by_collection?: AdminListingCollection[]
	top_active?: AdminTopListing[]
}

export interface AdminPrivateRelaySummary {
	submitted: number
	mined: number
	pending: number
	stale_pending: number
	avg_wait_min: number | null
	median_wait_min: number | null
	p90_wait_min: number | null
	under_30m: number
	over_4h: number
	first_submitted: string | null
}

export interface AdminBtcSwapTotals {
	created: number
	pending: number
	completed: number
	refunded: number
	failed: number
	expired: number
	creation_uncertain: number
	completed_sats: number
	unverified_payouts: number
	/** Estimated partner commission on completed, verified payouts; null means unknown. */
	commission_sats: number | null
	/** Completed orders with a reported partner rate, including a confirmed zero rate. */
	commission_orders: number
}

export interface AdminBtcSwapOrder {
	provider: string
	order_id: string | null
	attempt_id: string
	status: string
	from_currency: string
	from_network: string
	requested_amount: string
	payout_address: string
	actual_sats: number | null
	payout_txid: string | null
	commission_sats: number | null
	created_at: string
	updated_at: string
}

export interface AdminBtcSwaps {
	available: boolean
	/** Production orders created in the selected window; latest known state. */
	summary?: AdminBtcSwapTotals
	providers?: Array<AdminBtcSwapTotals & { provider: string }>
	/** The latest 50 orders in the selected creation window. */
	recent?: AdminBtcSwapOrder[]
	last_reconciled_at?: string | null
	/** Coverage is described by commission_orders; this does not prove a partner withdrawal. */
	commission_available: boolean
}

export interface AdminMetrics {
	range: AdminRange
	since: string
	until: string
	bucket: 'hour' | 'day' | 'week' | 'month'
	previous_since: string | null
	previous_until: string | null
	btc_usd: number
	generated_at: string
	cache_ttl_seconds: number
	summary: AdminTotals
	previous: AdminTotals | null
	series: AdminSeriesPoint[]
	sources: AdminSource[]
	top_collections: AdminTopCollection[]
	recent_snipes: AdminSnipe[]
	/** Sniped sales grouped by collection, worst first. Absent on older API builds. */
	snipes_by_collection?: AdminSnipeCollection[]
	audit: {
		last_audited_sale_at: string | null
		audited_sales: number
		unaudited_sales: number
		sniped_sales: number
		daemon_running?: boolean
		v2?: AdminAuditV2
	}
	moonpay?: AdminMoonpay
	tool_fees?: {
		available: boolean
		/** History still being read from the chain; totals are partial. */
		backfilling?: boolean
		events?: number
		synced_seconds_ago?: number | null
		last_error?: string | null
		/** What the fee-paying transactions were for, biggest first. */
		by_purpose?: AdminToolFeePurpose[]
		recent?: AdminToolFeeEvent[]
		diesel?: {
			units: number
			diesel: number
			usd: number
			price_usd: number | null
			traced: number
			txs: number
			vault: string
			vault_balance?: number | null
			vault_balance_usd?: number | null
			vault_change_in_window?: number | null
			vault_change_usd?: number | null
			vault_explorer?: string
		}
	}
	wallet_health?: AdminWalletHealth
	/** OW-created crypto-to-BTC deposits, excluding preview/test orders. */
	btc_swaps?: AdminBtcSwaps
	/** Purchases sent privately to miners (SpiderPool, MARA): time to mine, by pool. */
	private_relay?: {
		available: boolean
		stale_hours?: number
		all_time?: AdminPrivateRelaySummary
		window?: AdminPrivateRelaySummary
		by_pool?: Array<{ pool: string; mined: number; avg_wait_min: number | null; median_wait_min: number | null }>
		recent?: Array<{ txid: string; pool: string; submitted_at: string; block_height: number; wait_min: number }>
	}
	/** The wallet page's split-your-UTXO prompt: funnel and biggest single UTXOs. */
	split_prompt?: {
		available: boolean
		threshold_sats?: number
		attribution_days?: number
		funnel?: {
			measured: number
			eligible: number
			shown: number
			clicked: number
			dismissed: number
			split: number
			split_fee_sats: number
		}
		holders?: Array<{
			address: string
			wallet: string
			largest_sats: number
			total_sats: number
			utxo_count: number
			measured_at: string
			shown: boolean
			clicked: boolean
			split: boolean
		}>
	}
	/** Where people act: page views and clicks from the site's page tracker. */
	pages?: {
		available: boolean
		pages?: Array<{ path: string; views: number; sessions: number; clicks: number }>
		clicks?: Array<{ path: string; label: string; href: string; clicks: number; sessions: number }>
		wallets?: Array<{ wallet: string; sessions: number }>
	}
	/** Protected-listing plumbing state the API can see from the mempool. */
	passthrough_health?: {
		orphaned_parents: Array<{ inscription_id: string; parent_txid: string }>
		hidden_mempool_spent: number
	}
	fee_wallets?: AdminFeeWallet[]
	/** Latest completed sales with their fee. Absent on older API builds. */
	marketplace_fee_items?: AdminFeeItem[]
	/** Inscribe orders and what generated the fees. Absent on older API builds. */
	inscribe?: AdminInscribe
	/** New-listing supply: counts, ask value, most-listed collections. Absent on older API builds. */
	listings?: AdminListings
}

export class AdminUnauthorizedError extends Error {
	constructor() {
		super('unauthorized')
		this.name = 'AdminUnauthorizedError'
	}
}

async function checkFetcher(url: string): Promise<boolean> {
	const res = await fetch(url, { cache: 'no-store' })
	if (!res.ok) return false
	const body = await res.json()
	return body?.admin === true
}

/// Cheap allowlist membership check. Drives whether the Settings entry point
/// renders; the data route re-verifies with a signature so this is UI only.
const CHECK_PREFIX = 'ow::admin-check::'

function readCachedCheck(address: string): boolean | undefined {
	if (typeof window === 'undefined') return undefined
	try {
		const raw = localStorage.getItem(`${CHECK_PREFIX}${address}`)
		return raw === null ? undefined : raw === '1'
	} catch {
		return undefined
	}
}

export function useAdminCheck(address?: string | null) {
	const key = address ? `${API_URL}/admin/check/${address}` : null
	const fallbackData = address ? readCachedCheck(address) : undefined
	const { data, isLoading } = useSwr(key, checkFetcher, {
		revalidateOnFocus: false,
		dedupingInterval: 5 * 60_000,
		fallbackData,
		onSuccess: (ok) => {
			if (!address) return
			try {
				localStorage.setItem(`${CHECK_PREFIX}${address}`, ok ? '1' : '0')
			} catch {}
		}
	})
	return { isAdmin: data === true, isLoading: !!key && isLoading && data === undefined }
}

interface MetricsKey {
	address: string
	signature: string
	range: AdminRange
}

async function metricsFetcher([, key]: [string, MetricsKey]): Promise<AdminMetrics> {
	const res = await fetch(`${API_URL}/admin/metrics`, {
		method: 'POST',
		headers: { 'content-type': 'application/json' },
		cache: 'no-store',
		body: JSON.stringify({
			address: key.address,
			signature: key.signature,
			range: key.range
		})
	})
	if (res.status === 404) throw new AdminUnauthorizedError()
	if (!res.ok) throw new Error(`admin metrics HTTP ${res.status}`)
	const body = await res.json()
	if (!body || !Array.isArray(body.series) || !body.summary) {
		throw new Error(body?.message || 'admin metrics response malformed')
	}
	return body as AdminMetrics
}

const CACHE_PREFIX = 'ow::admin-metrics::v3::'
const CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
/// Earlier cache generations, bucketed by UTC day before the API moved to
/// New York days; dropped on first read so they don't sit in storage.
const OLD_CACHE_PREFIXES = ['ow::admin-metrics::v2::', 'ow::admin-market::v1::']
let oldCachesDropped = false

function dropOldCaches() {
	if (oldCachesDropped) return
	oldCachesDropped = true
	try {
		Object.keys(localStorage)
			.filter((k) => OLD_CACHE_PREFIXES.some((p) => k.startsWith(p)))
			.forEach((k) => localStorage.removeItem(k))
	} catch {}
}

/// Last good response per (address, range), so a reload paints the previous
/// numbers instantly and revalidates in the background. Stale data is
/// labelled by `generated_at`, never hidden.
function readCachedMetrics(address: string, range: AdminRange): AdminMetrics | undefined {
	if (typeof window === 'undefined') return undefined
	dropOldCaches()
	try {
		const raw = localStorage.getItem(`${CACHE_PREFIX}${address}::${range}`)
		if (!raw) return undefined
		const parsed = JSON.parse(raw) as { at: number; data: AdminMetrics }
		if (!parsed?.data?.summary || Date.now() - parsed.at > CACHE_MAX_AGE_MS) return undefined
		return parsed.data
	} catch {
		return undefined
	}
}

function writeCachedMetrics(address: string, range: AdminRange, data: AdminMetrics) {
	try {
		localStorage.setItem(
			`${CACHE_PREFIX}${address}::${range}`,
			JSON.stringify({ at: Date.now(), data })
		)
	} catch {}
}

export function clearCachedMetrics() {
	try {
		Object.keys(localStorage)
			.filter((k) => k.startsWith(CACHE_PREFIX))
			.forEach((k) => localStorage.removeItem(k))
	} catch {}
}

export default function useAdminMetrics(
	address: string | null | undefined,
	signature: string | null | undefined,
	range: AdminRange,
	onUnauthorized?: () => void
) {
	const key: [string, MetricsKey] | null =
		address && signature ? ['/admin/metrics', { address, signature, range }] : null
	const fallbackData = address ? readCachedMetrics(address, range) : undefined
	return useSwr(key, metricsFetcher, {
		revalidateOnFocus: true,
		revalidateOnMount: true,
		refreshInterval: 30_000,
		keepPreviousData: true,
		fallbackData,
		shouldRetryOnError: (err) => !(err instanceof AdminUnauthorizedError),
		onSuccess: (data) => {
			if (address) writeCachedMetrics(address, range, data)
		},
		onError: (err) => {
			if (err instanceof AdminUnauthorizedError) {
				clearCachedMetrics()
				onUnauthorized?.()
			}
		}
	})
}

// ─── Cross-marketplace intelligence ─────────────────────────────────

export interface MarketTally {
	sales: number
	volume_sats: number
}

export interface MarketVenue {
	key: string
	label: string
	sales: number
	volume_sats: number
	avg_price_sats: number
	unique_buyers: number
	unique_sellers: number
	prev_sales: number
	prev_volume_sats: number
	volume_share_pct: number
	sales_share_pct: number
}

export interface MarketCollection {
	slug: string
	name: string | null
	icon: string | null
	sales: number
	volume_sats: number
	avg_price_sats: number
	unique_buyers: number
	prev_sales: number
	prev_volume_sats: number
	ow_sales: number
	ow_volume_sats: number
	ow_share_pct: number
	by: Record<string, MarketTally>
}

export interface MarketRecentSale {
	t: string
	height: number
	marketplace: number
	marketplace_label: string
	inscription_id: string | null
	price_sats: number
	collection_slug: string | null
	collection_name: string | null
}

export interface AdminMarket {
	range: string
	since: string
	until: string
	bucket: 'hour' | 'day'
	requested_days: number | null
	detail_window_days: number
	clipped: boolean
	generated_at: string
	coverage: {
		blocks_in_window: number
		from_height: number | null
		to_height: number | null
		blocks_cached: number
		oldest_cached_at: string | null
		last_warm_seconds_ago: number | null
		last_error: string | null
	}
	total: { sales: number; volume_sats: number; unique_buyers: number; unique_sellers: number }
	marketplaces: MarketVenue[]
	series: Array<{ t: string; by: Record<string, MarketTally> }>
	collections: MarketCollection[]
	opportunities: MarketCollection[]
	trending: MarketCollection[]
	recent: MarketRecentSale[]
}

const MARKET_PREFIX = 'ow::admin-market::v2::'

function readCachedMarket(address: string, range: AdminRange): AdminMarket | undefined {
	if (typeof window === 'undefined') return undefined
	try {
		const raw = localStorage.getItem(`${MARKET_PREFIX}${address}::${range}`)
		if (!raw) return undefined
		const parsed = JSON.parse(raw) as { at: number; data: AdminMarket }
		if (!parsed?.data?.marketplaces || Date.now() - parsed.at > CACHE_MAX_AGE_MS) return undefined
		return parsed.data
	} catch {
		return undefined
	}
}

async function marketFetcher([, key]: [string, MetricsKey]): Promise<AdminMarket> {
	const res = await fetch(`${API_URL}/admin/market`, {
		method: 'POST',
		headers: { 'content-type': 'application/json' },
		cache: 'no-store',
		body: JSON.stringify({ address: key.address, signature: key.signature, range: key.range })
	})
	if (res.status === 404) throw new AdminUnauthorizedError()
	if (!res.ok) throw new Error(`admin market HTTP ${res.status}`)
	const body = await res.json()
	if (!body || !Array.isArray(body.marketplaces)) {
		throw new Error(body?.message || 'admin market response malformed')
	}
	return body as AdminMarket
}

export function useAdminMarket(
	address: string | null | undefined,
	signature: string | null | undefined,
	range: AdminRange,
	onUnauthorized?: () => void
) {
	const key: [string, MetricsKey] | null =
		address && signature ? ['/admin/market', { address, signature, range }] : null
	const fallbackData = address ? readCachedMarket(address, range) : undefined
	return useSwr(key, marketFetcher, {
		revalidateOnFocus: true,
		refreshInterval: 60_000,
		keepPreviousData: true,
		fallbackData,
		shouldRetryOnError: (err) => !(err instanceof AdminUnauthorizedError),
		onSuccess: (data) => {
			if (!address) return
			try {
				localStorage.setItem(
					`${MARKET_PREFIX}${address}::${range}`,
					JSON.stringify({ at: Date.now(), data })
				)
			} catch {}
		},
		onError: (err) => {
			if (err instanceof AdminUnauthorizedError) onUnauthorized?.()
		}
	})
}
